import coPilotSrc from '@assets/images/co-pilot.png';
import { cn, Stack, StackProps, Tooltip, TooltipContent, TooltipTrigger } from 'component-library';
import { PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RecommendationCard } from '@/components//widgets/o/RecommendationCard/types';
import { SidebarToggleButton } from '@/components/widgets/a/Button/SidebarToggleButton';

import { CoPilotContextProvider } from '../CoPilotContextProvider';
import { TokenUsageSummary } from './TokenUsage';

export type CoPilotProps = {
  isCollapsed?: boolean;
  onCollapse?: () => void;
  className?: string;
};

type CoPilotLogoProps = {
  showText?: boolean;
  spacing?: number;
  direction?: 'row' | 'column';
  iconSize?: number;
  textStyle?: string;
} & StackProps;

export const CoPilotLogo = ({
  showText = true,
  spacing = 2,
  direction = 'row',
  iconSize = 48,
  textStyle,
  ...props
}: CoPilotLogoProps) => {
  const { t } = useTranslation();

  return (
    <Tooltip>
      <TooltipTrigger className='items-center'>
        <Stack spacing={spacing} direction={direction} centerMain className='h-22' {...props}>
          <img
            src={coPilotSrc}
            alt={t('global.copilot.title')}
            style={{ width: `${iconSize}px`, height: `${iconSize}px` }}
          />
          {showText && (
            <span className={cn('typography-h3 text-secondary-100', textStyle)}>{t('global.copilot.title')}</span>
          )}
        </Stack>
      </TooltipTrigger>
      <TooltipContent className='whitespace-pre-wrap'>
        <Stack className='gap-4'>
          {t('global.copilot.tooltip.body')}
          <TokenUsageSummary />
        </Stack>
      </TooltipContent>
    </Tooltip>
  );
};

type CoPilotHeaderProps = PropsWithChildren<CoPilotProps> & { logoProps?: Partial<CoPilotLogoProps> };

export const CoPilotHeader = ({
  isCollapsed = false,
  onCollapse,
  className,
  logoProps,
  children,
}: CoPilotHeaderProps) => {
  return (
    <div className={cn('flex h-[88px] items-center justify-between border-b border-neutral-black-12 px-4', className)}>
      <CoPilotLogo data-testid='co-pilot-header' showText={!isCollapsed} {...logoProps} />
      {children}
      {!isCollapsed && onCollapse && (
        <SidebarToggleButton direction='right' onClick={onCollapse} data-testid='co-pilot-minimize-button' />
      )}
    </div>
  );
};

export const CoPilotEmptyContent = (props: CoPilotProps) => {
  const { t } = useTranslation();
  const [selectedRecommendationCard, setSelectedRecommendationCard] = useState<RecommendationCard | null>(null);

  return (
    <CoPilotContextProvider value={{ selectedRecommendationCard, setSelectedRecommendationCard }}>
      <CoPilotHeader {...props} />
      {!props.isCollapsed && (
        <div className='w-full p-6 border-b border-neutral-black-12 space-y-2'>
          <span className='typography-h4'>{t('global.copilot.noRecommendations')}</span>
          <p className='typography-body-2 text-text-secondary'>{t('global.copilot.noRecommendationsDescription')}</p>
        </div>
      )}
    </CoPilotContextProvider>
  );
};
